import "@grapecity/wijmo.styles/wijmo.css";
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { TreeView } from "@grapecity/wijmo.react.nav";
import styles from "./E3WijmoDataTree.module.css";
import { findAncestors, toHierarchy } from "e3-common/utils/dataUtil";

/**
 * E3WijmoDataTree
 * @type React.FC<E3WijmoDataTreePropTypes>
 */
const E3WijmoDataTree = React.forwardRef((props, ref) => {
  const refTree = useRef();
  const refExpand = useRef(props.expandOnLoaded);

  React.useImperativeHandle(ref, () => ({
    setDataBinding(source) {
      const treeData = toHierarchy(source, props.idName, props.parentIdName);
      let checkedItems = [];
      if (props.showCheckboxes) {
        checkedItems = source.filter((e) => e[props.checkedMemberPath]);
      }

      setCurrState((prev) => ({
        ...prev,
        originSource: source,
        treeData: treeData,
        checkedItems: checkedItems,
      }));
    },
    setSelectedItem(id) {
      const { originSource } = currState;
      const found = originSource.find((e) => e[props.idName] === id);
      if (found) {
        setCurrState((prev) => ({
          ...prev,
          selectedItem: found,
        }));
      }
    },
    getSelectedItem() {
      return currState.selectedItem;
    },
    getCheckedItems(includePaths = true) {
      // Tree 상 Check 된 Item의 상위 Node들도 가져오기
      if (includePaths) {
        const checkedIds = currState.checkedItems.map((item) => item[props.idName]);
        return findAncestors(currState.originSource, checkedIds, props.idName, props.parentIdName);
      }
      return currState.checkedItems;
    },

    setDefaultSelectedItem(selectItem = null) {
      if (selectItem) {
        setCurrState((prev) => ({
          ...prev,
          selectedItem: selectItem,
        }));
      }
    },

    changeExpanded(changeValue = null) {
      if (changeValue != null) refExpand.current = true;
    },
  }));

  /**
   * E3WijmoDataTree Component의 State
   */
  const [currState, setCurrState] = useState({
    originSource: [],
    treeData: [],
    selectedItem: null,
    checkedItems: [],
  });

  /**
   * @author 이동현
   */
  const _initialized = (ctrl) => {
    refTree.current = ctrl;
  };

  /**
   * @author 이동현
   */
  const _loadedItems = (ctrl) => {
    ctrl.collapseToLevel(props.isExpandAll ? 10000 : 0);

    if (props.loadedItems) {
      props.loadedItems(ctrl);
    }
  };

  const _selectedItemChanged = (e) => {
    setCurrState((prev) => ({
      ...prev,
      selectedItem: e.selectedItem,
    }));

    if (props.onSelectedItemChanged) {
      const selectedNodePath = _findSelectedNodePath(e.selectedNode);
      props.onSelectedItemChanged(e, selectedNodePath);
    }
  };

  const _findSelectedNodePath = (selectedNode) => {
    if (!selectedNode) {
      return [];
    }

    let path = [];
    path.push(selectedNode.dataItem);
    let curNode = selectedNode;

    while (curNode) {
      const parentNode = curNode.parentNode;
      if (!parentNode) break;

      path.unshift(parentNode.dataItem);
      curNode = parentNode;
    }

    return path;
  };

  const _onCheckedItemsChanged = (e) => {
    setCurrState((prev) => ({
      ...prev,
      checkedItems: e.checkedItems,
    }));

    if (props.checkedItemsChanged) {
      props.checkedItemsChanged(e);
    }
  };

  return (
    <div className={styles.container}>
      <TreeView
        className={styles.treeView}
        itemsSource={currState.treeData}
        isAnimated={false}
        displayMemberPath={props.displayMemberPath}
        childItemsPath={"children"}
        selectedItem={currState.selectedItem}
        showCheckboxes={props.showCheckboxes}
        checkedMemberPath={props.checkedMemberPath}
        autoCollapse={false}
        expandOnLoad={refExpand.current}
        /**
         * @author 이동현
         */
        initialized={_initialized}
        loadedItems={_loadedItems}
        selectedItemChanged={_selectedItemChanged}
        checkedItemsChanged={_onCheckedItemsChanged}
      />
    </div>
  );
});

const E3WijmoDataTreePropTypes =
  {
    /**
     * 화면에 출력되는 Text 항목
     */
    displayMemberPath: PropTypes.string,
    idName: PropTypes.string,
    parentIdName: PropTypes.string,
    onSelectedItemChanged: PropTypes.func,
    expandOnLoaded: PropTypes.bool,
    /**
     * @author 이동현
     */
    isExpandAll: PropTypes.bool,
  } &
  /**
   * @author 이동현
   */
  TreeView;

E3WijmoDataTree.propTypes = E3WijmoDataTreePropTypes;
E3WijmoDataTree.defaultProps = {
  displayMemberPath: "name",
  idName: "id",
  parentIdName: "parentId",
  onSelectedItemChanged: null,
  showCheckboxes: false,
  checkedMemberPath: "chk",
  expandOnLoaded: false,
  isExpandAll: true,
};
E3WijmoDataTree.displayName = "E3WijmoDataTree";
export default E3WijmoDataTree;
